import { AxiosError } from "axios";
import { AnsweringClient } from "../clients/answeringClient";

export interface IAnswer {
  description: string;
  order: number;
  selected: boolean;
  text: string;
  icon: string;
  allow_audio: boolean;
  icon_size: string;
}

export interface IQuestion {
  answers: IAnswer[];
  description: string;
  order: number;
  pattern: string;
  title: string;
  type: string;
  audio: boolean;
  text_to_audio: string;
  question_config?: Record<string, unknown>;
}

export interface QuestionsControllerDelegate {
  genericError(message?: string): void;
  didGetQuestion(question: IQuestion): void;
  didSaveAnswer(): void;
  didEndInstrument(): void;
  applicationShouldLogout(): void;
}

export class QuestionsController {
  constructor(
    private delegate: QuestionsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getNextQuestion(
    authorizationToken: string,
    assignmentToken: string,
    type: string
  ) {
    try {
      let response;
      if (type === "linear") {
        response = await this.answeringClient.getNextQuestion(
          authorizationToken,
          assignmentToken,
          "next"
        );
      } else {
        response = await this.answeringClient.getNextQuestion(
          authorizationToken,
          assignmentToken,
          type
        );
      }
      this.delegate.didGetQuestion(response.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        const response = error.response;
        if (response?.status === 400 && response?.data.code === 20001) {
          this.delegate.didEndInstrument();
          return;
        }

        if (response?.status === 403) {
          const newTokens = await this.refreshTokens();
          if (!newTokens) {
            return;
          }
          this.getNextQuestion(newTokens.accessToken, assignmentToken, type);
          return;
        }
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  public async sendAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    answerOrders: number[]
  ) {
    try {
      await this.answeringClient.sendAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        answerOrders
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.sendAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          answerOrders
        );
        return;
      } else if (
        error?.response?.status === 400 &&
        error?.response?.data.code === 20001
      ) {
        this.delegate.didEndInstrument();
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  public async sendFreeTextAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    answer: string
  ) {
    try {
      await this.answeringClient.sendFreeTextAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        answer
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.sendFreeTextAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          answer
        );
        return;
      } else if (
        error?.response?.status === 400 &&
        error?.response?.data.code === 20001
      ) {
        this.delegate.didEndInstrument();
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  public async sendTaskAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    taskAnswer: object
  ) {
    try {
      await this.answeringClient.sendTaskAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        taskAnswer
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.sendTaskAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          taskAnswer
        );
        return;
      } else if (
        error?.response?.status === 400 &&
        error?.response?.data.code === 20001
      ) {
        this.delegate.didEndInstrument();
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }

  public async sendAudioAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    audioBlob: Blob
  ) {
    try {
      await this.answeringClient.sendAudioAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        audioBlob
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        this.sendAudioAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          audioBlob
        );
        return;
      } else if (
        error?.response?.status === 400 &&
        error?.response?.data.code === 20001
      ) {
        this.delegate.didEndInstrument();
        return;
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  private extractErrorMessage(error: any): string | undefined {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
    );
  }
}
